/*
 * File: AppNavigation.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on February 4, 2022 at 4:29 PM
 * Copyright © 2022 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 4, 2025 at 5:44 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useState,
	forwardRef,
	useEffect,
	useMemo,
} from 'react';
import PropTypes from 'prop-types';

// Components
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { LeavePageWidget } from '../LeavePageWidget';
import { Footer } from '../Footer';

// Styles
import * as S from './AppNavigation.styles';


/**
 * Component
 */

export const AppNavigation = forwardRef(({
	children, contentPadding, isFixed, showFooter, containerClassName, hideMobileNavigation
}, ref) => {

	// Set state
	const [sideNavigationOpen, setSideNavigationOpen] = useState(false);

	// Check if site is embedded
	const isEmbedded = useMemo(() => {
		if (typeof window !== 'undefined') {
			return window.self !== window.top;
		}
		return false;
	}, []);

	// Toggle navigation function
	const toggleNavigation = () => {

		// Toggle mobile sidebar visibility
		setSideNavigationOpen(!sideNavigationOpen);
	};

	// Handle component initialization
	useEffect(() => {}, []);

	// Render component
	return (
		<S.Navigation ref={ref} className={containerClassName}>

			{/* Top Bar */}
			{!isEmbedded && (
				<Topbar
					toggleNavigation={toggleNavigation}
					isFixed={isFixed}
					hideMobileNavigation={hideMobileNavigation}
				/>
			)}

			{/* Side Navigation */}
			{!isEmbedded && (
				<S.NavigationOverlay $isOpen={sideNavigationOpen}>
					<Sidebar
						className="leftSidebar"
						toggleOverlay={toggleNavigation}
					/>
					<S.Overlay $isOpen={sideNavigationOpen} onClick={toggleNavigation} />
				</S.NavigationOverlay>
			)}

			{/* Content */}
			<S.Wrapper>
				<S.Content id="scroller" $isFixed={isFixed}>

					{/* Content */}
					{contentPadding ? (
						<S.ContentPadding left right>
							{children}
						</S.ContentPadding>
					)
						: children}

				</S.Content>
			</S.Wrapper>

			{/* Footer */}
			{showFooter && <Footer />}

			{/* Leave Page Widget */}
			{!isEmbedded && <LeavePageWidget />}

		</S.Navigation>
	);
});


/**
 * Configuration
 */

AppNavigation.displayName = 'AppNavigation';
AppNavigation.propTypes = {
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
	contentPadding: PropTypes.bool,
	containerClassName: PropTypes.string,
	isFixed: PropTypes.bool,
	hideMobileNavigation: PropTypes.bool,
	showFooter: PropTypes.bool
};
AppNavigation.defaultProps = {
	children: null,
	contentPadding: true,
	containerClassName: null,
	isFixed: true,
	hideMobileNavigation: false,
	showFooter: false
};
